import React, {useState, useCallback, useEffect, useMemo} from 'react';
import {ScrollView, View, TouchableOpacity} from "react-native";
import EStyleSheet from "react-native-extended-stylesheet";

import Modal from "../../../../../../components/Modal";
import MessagesService from "../../../../../../services/MessagesService";
import UsersService from "../../../../../../services/UsersService";
import ContentSection from "../../../../../../components/ContentSection";
import AppText from "../../../../../../components/AppText";
import {textStyles} from "../../../../../../styles/text";
import AppTextInput from "../../../../../../components/AppTextInput";
import ToggleField from "../../../../../../components/ToggleField";
import {basicStyles} from "../../../../../../styles/basic";
import AppButton from "../../../../../../components/AppButton";
import AppPickerInput from "../../../../../../components/AppPickerInput";
import ConditionModal from "./ConditionModal";
import StoreFrontsService from "../../../../../../services/StoreFrontsService";
import BlueCircleTrashSvg from "../../../../../../components/svgs/BlueCircleTrashSvg";

const getInitialAutomation = () => ({
    message_automation_title: '',
    message_automation_type_id: '',
    message_automation_action_id: '',
    message_automation_active: 1,
    conditions: [],
    message_automation_note: '',
    message_automation_assign_to_user_id: '',
    message_automation_assign_to_group_id: '',
    message_automation_send_text_to: '',
    message_automation_send_text_message: '',
    message_automation_send_email_to: '',
    message_automation_send_email_subject: '',
    message_automation_send_email_message: '',
});

function AutomationModal({onClose, onSubmit, organizationId, automationDefs, active = false, automationId = null}) {
    const [assignmentType, setAssignmentType] = useState('user');
    const [teamMembers, setTeamMembers] = useState([]);
    const [storeFronts, setStoreFronts] = useState([]);
    const [showConditionModal, setShowConditionModal] = useState(false);
    const [editingConditionIndex, setEditingConditionIndex] = useState(null);
    const [automation, setAutomation] = useState(getInitialAutomation);
    const [textRecipients, setTextRecipients] = useState(['']);
    const [emailRecipients, setEmailRecipients] = useState(['']);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const parseRecipientsCsv = useCallback((csvValue) => {
        if (!csvValue) {
            return [''];
        }

        const recipients = csvValue
            .split(',')
            .map(value => value.trim())
            .filter(Boolean);

        return recipients.length ? recipients : [''];
    }, []);

    const recipientsToCsv = useCallback((recipients) => {
        return recipients
            .map(value => value.trim())
            .filter(Boolean)
            .join(',');
    }, []);

    const updateAutomation = useCallback((prop, value) => {
        setAutomation(current => {
            let update = {...current};
            update[prop] = value;

            // Reset action-specific fields when action changes
            if (prop === 'message_automation_action_id') {
                update.message_automation_note = '';
                update.message_automation_assign_to_user_id = '';
                update.message_automation_assign_to_group_id = '';
                update.message_automation_send_text_to = '';
                update.message_automation_send_text_message = '';
                update.message_automation_send_email_to = '';
                update.message_automation_send_email_subject = '';
                update.message_automation_send_email_message = '';
                setTextRecipients(['']);
                setEmailRecipients(['']);
            }

            return update;
        });
    }, []);

    const openConditionModal = useCallback((index = null) => {
        setEditingConditionIndex(index);
        setShowConditionModal(true);
    }, []);

    const closeConditionModal = useCallback(() => {
        setShowConditionModal(false);
        setEditingConditionIndex(null);
    }, []);

    const handleConditionSubmit = useCallback((conditionData) => {
        setAutomation(current => {
            let update = {...current};
            if (editingConditionIndex !== null) {
                // Editing existing condition
                update.conditions[editingConditionIndex] = conditionData;
            } else {
                // Adding new condition - set default logical operator to AND for all conditions
                const newCondition = {
                    ...conditionData,
                    message_automation_condition_logical_operator_id: automationDefs?.logicalOperators?.find(op => op.message_automation_condition_logical_operator_slug === 'and')?.message_automation_condition_logical_operator_id?.toString() || '1'
                };
                update.conditions = [...current.conditions, newCondition];
            }
            return update;
        });
    }, [editingConditionIndex, automationDefs]);

    const removeCondition = useCallback((index) => {
        setAutomation(current => {
            let update = {...current};
            update.conditions = current.conditions.filter((_, i) => i !== index);
            return update;
        });
    }, []);

    const updateConditionLogic = useCallback((index, logicOperator) => {
        setAutomation(current => {
            let update = {...current};
            update.conditions[index] = {...current.conditions[index], message_automation_condition_logical_operator_id: logicOperator};
            return update;
        });
    }, []);

    const handleClose = useCallback(() => {
        onClose();
        setAutomation(getInitialAutomation());
        setTextRecipients(['']);
        setEmailRecipients(['']);
        setAssignmentType('user');
        setIsSubmitting(false);
    }, [onClose]);

    const handleSubmit = useCallback(() => {
        if (!isSubmitting) {
            setIsSubmitting(true);
            const automationPayload = {
                ...automation,
                message_automation_send_text_to: recipientsToCsv(textRecipients),
                message_automation_send_email_to: recipientsToCsv(emailRecipients),
            };
            let promise;
            if (automationId) {
                promise = MessagesService.updateAutomation(organizationId, automationId, automationPayload);
            } else {
                promise = MessagesService.insertAutomation(organizationId, automationPayload);
            }

            promise.then((automation) => {
                onSubmit(automation);
                handleClose();
            }).catch(() => {
                setIsSubmitting(false);
            });
        }
    }, [automation, automationId, organizationId, onSubmit, handleClose, isSubmitting, textRecipients, emailRecipients, recipientsToCsv])

    useEffect(() => {
        if (automationId) {
            console.log('Loading automation with ID:', automationId);
            MessagesService.getAutomation(organizationId, automationId)
                .then((response) => {
                    const automationData = response.automation;

                    if (automationData) {
                        setAutomation({
                            ...getInitialAutomation(),
                            ...automationData,
                        });
                        setTextRecipients(parseRecipientsCsv(automationData.message_automation_send_text_to));
                        setEmailRecipients(parseRecipientsCsv(automationData.message_automation_send_email_to));

                        if (automationData.message_automation_assign_to_group_id) {
                            setAssignmentType('group');
                        } else {
                            setAssignmentType('user');
                        }
                    }
                })
                .catch((error) => {
                    console.error('Error loading automation:', error);
                })
        } else {
            setAutomation(getInitialAutomation());
            setTextRecipients(['']);
            setEmailRecipients(['']);
            setAssignmentType('user');
        }
    }, [organizationId, automationId, parseRecipientsCsv]);

    useEffect(() => {
        // Load team members and roles
        const loadData = async () => {
            try {
                const {users} = await UsersService.getMessageflowUsers(organizationId);
                if (users) {
                    setTeamMembers(users);
                }

                const storeFronts = await StoreFrontsService.getStoreFronts(organizationId);
                if (storeFronts) {
                    setStoreFronts(storeFronts);
                }
            } catch (error) {
                console.error('Error loading team members and roles:', error);
            }
        };
        
        if (organizationId) {
            loadData();
        }
    }, [organizationId]);

    const getConditionTitle = useCallback(condition => {
        // Find the condition type title
        const conditionType = automationDefs?.conditionTypes?.find(
            type => type.message_automation_condition_type_id.toString() === condition.message_automation_condition_type_id?.toString()
        );
        const typeTitle = conditionType?.message_automation_condition_type_title || 'Unknown Field';

        // Find the operator title
        const operator = automationDefs?.conditionOperators?.find(
            op => op.message_automation_condition_operator_id.toString() === condition.message_automation_condition_operator_id?.toString()
        );
        const operatorTitle = operator?.message_automation_condition_operator_title || 'Unknown Operator';

        // Get the value
        const value = condition.message_automation_condition_value || '';

        return `${typeTitle} ${operatorTitle} "${value}"`;
    }, [automationDefs]);

    const selectedActionSlug = useMemo(() => {
        const selectedAction = automationDefs?.actions?.find(
            action => action.message_automation_action_id.toString() === automation.message_automation_action_id.toString()
        );

        return selectedAction?.message_automation_action_slug || null;
    }, [automationDefs?.actions, automation.message_automation_action_id]);

    const updateTextRecipient = useCallback((index, value) => {
        setTextRecipients(current => current.map((recipient, currentIndex) => (
            currentIndex === index ? value : recipient
        )));
    }, []);

    const addTextRecipient = useCallback(() => {
        setTextRecipients(current => [...current, '']);
    }, []);

    const removeTextRecipient = useCallback((index) => {
        setTextRecipients(current => {
            const updated = current.filter((_, currentIndex) => currentIndex !== index);
            return updated.length ? updated : [''];
        });
    }, []);

    const updateEmailRecipient = useCallback((index, value) => {
        setEmailRecipients(current => current.map((recipient, currentIndex) => (
            currentIndex === index ? value : recipient
        )));
    }, []);

    const addEmailRecipient = useCallback(() => {
        setEmailRecipients(current => [...current, '']);
    }, []);

    const removeEmailRecipient = useCallback((index) => {
        setEmailRecipients(current => {
            const updated = current.filter((_, currentIndex) => currentIndex !== index);
            return updated.length ? updated : [''];
        });
    }, []);

    return (
        <Modal
            active={active}
            onClose={handleClose}
            innerStyle={{width: '100%', alignItems: 'center'}}
        >
            <ContentSection
                style={[styles.contentWrapper]}
                onClick={e => {
                    e.stopPropagation();
                }}
            >
                <ScrollView>
                    <AppText style={[textStyles.pageTitle, {marginBottom: 20}]}>
                        {automationId ? 'Update' : 'Create New'} Automation
                    </AppText>

                    <AppTextInput
                        label="Title"
                        value={automation.message_automation_title}
                        onChangeText={(value) => updateAutomation('message_automation_title', value)}
                        wrapperStyle={styles.inputWrapper}
                        placeholder="Enter automation title"
                    />

                    {/* Type Selection */}
                    <View style={styles.section}>
                        <AppPickerInput
                            label="Type"
                            selectedValue={automation.message_automation_type_id?.toString() ?? ''}
                            onValueChange={(value) => updateAutomation('message_automation_type_id', value)}
                            items={automationDefs?.types?.map(type => ({
                                label: type.message_automation_type_title,
                                value: type.message_automation_type_id.toString()
                            })) || []}
                            labelExtractor="label"
                            valueExtractor="value"
                            wrapperStyle={styles.inputWrapper}
                        />
                    </View>

                    {/* Conditions */}
                    <View style={styles.section}>
                        <AppText style={[styles.sectionTitle, {marginBottom: 15}]}>Conditions</AppText>

                        {/* And/Or Field if not first condition */}
                        {automation.conditions.map((condition, index) => (
                            <View key={index}>
                                {index > 0 && (
                                    <View style={styles.logicOperatorContainer}>
                                        <AppPickerInput
                                            title="Logic"
                                            selectedValue={condition.message_automation_condition_logical_operator_id?.toString() ?? "1"}
                                            onValueChange={(value) => updateConditionLogic(index, value)}
                                            items={automationDefs?.logicalOperators?.map(operator => ({
                                                label: operator.message_automation_condition_logical_operator_title,
                                                value: operator.message_automation_condition_logical_operator_id.toString()
                                            })) || []}
                                            labelExtractor="label"
                                            valueExtractor="value"
                                            wrapperStyle={styles.logicOperator}
                                        />
                                    </View>
                                )}
                                {/* Condition Fieldset */}
                                <View style={styles.conditionFieldset}>
                                    <View style={styles.conditionHeader}>
                                        <AppText style={styles.conditionTitle}>
                                            {getConditionTitle(condition)}
                                        </AppText>
                                        <View style={styles.conditionActions}>
                                            <TouchableOpacity 
                                                style={styles.editButton}
                                                onPress={() => openConditionModal(index)}
                                            >
                                                <AppText style={styles.editButtonText}>Edit</AppText>
                                            </TouchableOpacity>
                                            <TouchableOpacity 
                                                style={styles.removeButton}
                                                onPress={() => removeCondition(index)}
                                            >
                                                <AppText style={styles.removeButtonText}>Remove</AppText>
                                            </TouchableOpacity>
                                        </View>
                                    </View>
                                </View>
                            </View>
                        ))}
                        <AppButton
                            label="Add Condition"
                            action={() => openConditionModal()}
                            theme="transBlue"
                            style={styles.addButton}
                        />
                    </View>

                    {/* Actions */}
                    <View style={styles.section}>
                        <AppPickerInput
                            label="Action"
                            selectedValue={automation.message_automation_action_id?.toString() ?? ''}
                            onValueChange={(value) => updateAutomation('message_automation_action_id', value)}
                            items={automationDefs?.actions?.map(action => ({
                                label: action.message_automation_action_title,
                                value: action.message_automation_action_id.toString()
                            })) || []}
                            labelExtractor="label"
                            valueExtractor="value"
                            wrapperStyle={styles.inputWrapper}
                        />

                        {/* Action-specific fields */}
                        {selectedActionSlug === 'add-note' && (
                            <AppTextInput
                                label="Note Text"
                                value={automation.message_automation_note}
                                onChangeText={(value) => updateAutomation('message_automation_note', value)}
                                wrapperStyle={styles.inputWrapper}
                                multiline
                                numberOfLines={2}
                            />
                        )}

                        {selectedActionSlug === 'assign-task' && (
                            <View>
                                <AppPickerInput
                                    label="Assignment Type"
                                    selectedValue={assignmentType}
                                    onValueChange={(value) => {
                                        setAssignmentType(value)
                                        if (value !== 'user') {
                                            updateAutomation('message_automation_assign_to_user_id', null);
                                        }
                                        if (value !== 'group') {
                                            updateAutomation('message_automation_assign_to_group_id', null);
                                        }
                                    }}
                                    items={[
                                        {
                                            label: 'User',
                                            value: 'user',
                                        },
                                        {
                                            label: 'Group',
                                            value: 'group',
                                        },
                                    ]}
                                    labelExtractor="label"
                                    valueExtractor="value"
                                    wrapperStyle={styles.inputWrapper}
                                />

                                {/* add selector for user or group */}
                                {assignmentType === 'user' && (
                                    <AppPickerInput
                                        label="Assign to Team Member"
                                        selectedValue={automation.message_automation_assign_to_user_id?.toString() ?? ''}
                                        onValueChange={(value) => updateAutomation('message_automation_assign_to_user_id', value)}
                                        items={teamMembers.map(user => ({
                                            label: `${user.profile_first_name} ${user.profile_last_name}`,
                                            value: user.user_id.toString()
                                        }))}
                                        labelExtractor="label"
                                        valueExtractor="value"
                                        wrapperStyle={styles.inputWrapper}
                                    />
                                )}

                                {assignmentType === 'group' && (
                                    <AppPickerInput
                                        label="Assign to Group"
                                        selectedValue={automation.message_automation_assign_to_group_id?.toString() ?? ''}
                                        onValueChange={(value) => updateAutomation('message_automation_assign_to_group_id', value)}
                                        items={storeFronts.map(storeFront => ({
                                            label: storeFront.store_front_title,
                                            value: storeFront.store_front_id.toString(),
                                        }))}
                                        labelExtractor="label"
                                        valueExtractor="value"
                                        wrapperStyle={styles.inputWrapper}
                                    />
                                )}
                            </View>
                        )}

                        {selectedActionSlug === 'send-text' && (
                            <View>
                                {textRecipients.map((recipient, index) => (
                                    <AppTextInput
                                        key={index}
                                        label={index === 0 ? 'To Phone Number' : `To Phone Number ${index + 1}`}
                                        value={recipient}
                                        onChangeText={(value) => updateTextRecipient(index, value)}
                                        wrapperStyle={styles.inputWrapper}
                                        placeholder="Enter phone number"
                                        actionButton={textRecipients.length > 1 ? (
                                            <TouchableOpacity
                                                style={styles.removeRecipientButton}
                                                onPress={() => removeTextRecipient(index)}
                                            >
                                                <BlueCircleTrashSvg width={24} height={24} />
                                            </TouchableOpacity>
                                        ) : null}
                                    />
                                ))}
                                <AppButton
                                    label="Add Phone Number"
                                    action={addTextRecipient}
                                    theme="transBlue"
                                    style={styles.addRecipientButton}
                                />
                                <AppTextInput
                                    label="Text Message"
                                    value={automation.message_automation_send_text_message}
                                    onChangeText={(value) => updateAutomation('message_automation_send_text_message', value)}
                                    wrapperStyle={[styles.inputWrapper, styles.multilineInputWrapper]}
                                    labelStyle={styles.multilineInputLabel}
                                    inputStyle={styles.multilineInput}
                                    multiline
                                    numberOfLines={4}
                                />
                            </View>
                        )}

                        {selectedActionSlug === 'send-email' && (
                            <View>
                                {emailRecipients.map((recipient, index) => (
                                    <AppTextInput
                                        key={index}
                                        label={index === 0 ? 'To Email Address' : `To Email Address ${index + 1}`}
                                        value={recipient}
                                        onChangeText={(value) => updateEmailRecipient(index, value)}
                                        wrapperStyle={styles.inputWrapper}
                                        placeholder="Enter email address"
                                        actionButton={emailRecipients.length > 1 ? (
                                            <TouchableOpacity
                                                style={styles.removeRecipientButton}
                                                onPress={() => removeEmailRecipient(index)}
                                            >
                                                <BlueCircleTrashSvg width={24} height={24} />
                                            </TouchableOpacity>
                                        ) : null}
                                    />
                                ))}
                                <AppButton
                                    label="Add Email Address"
                                    action={addEmailRecipient}
                                    theme="transBlue"
                                    style={styles.addRecipientButton}
                                />
                                <AppTextInput
                                    label="Email Subject"
                                    value={automation.message_automation_send_email_subject}
                                    onChangeText={(value) => updateAutomation('message_automation_send_email_subject', value)}
                                    wrapperStyle={styles.inputWrapper}
                                />
                                <AppTextInput
                                    label="Email Message"
                                    value={automation.message_automation_send_email_message}
                                    onChangeText={(value) => updateAutomation('message_automation_send_email_message', value)}
                                    wrapperStyle={[styles.inputWrapper, styles.multilineInputWrapper]}
                                    labelStyle={styles.multilineInputLabel}
                                    inputStyle={styles.multilineInput}
                                    multiline
                                    numberOfLines={4}
                                />
                            </View>
                        )}
                    </View>

                    <ToggleField
                        label="Enabled"
                        value={parseInt(automation.message_automation_active) === 1}
                        onChange={value => {
                            updateAutomation('message_automation_active', value ? 1 : 0);
                        }}
                        wrapperStyle={styles.inputWrapper}
                    />

                    <View
                        style={[basicStyles.flexRow, basicStyles.justifyContentSpaceBetween, basicStyles.alignContentCenter, {marginTop: 20}]}>
                        <View style={[basicStyles.flexRow]}>
                            <AppButton
                                label="Cancel"
                                action={handleClose}
                                style={{marginRight: 10}}
                                theme="transBlue"
                                disabled={isSubmitting}
                            />
                            <AppButton
                                label="Confirm"
                                action={handleSubmit}
                                disabled={isSubmitting}
                            />
                        </View>
                    </View>
                </ScrollView>
            </ContentSection>
            
            {showConditionModal && (
                <ConditionModal
                    onClose={closeConditionModal}
                    onSubmit={handleConditionSubmit}
                    conditionOperators={automationDefs?.conditionOperators}
                    condition={editingConditionIndex !== null ? automation.conditions[editingConditionIndex] : null}
                    isEdit={editingConditionIndex !== null}
                    automationDefs={automationDefs}
                />
            )}
        </Modal>
    );
}

export default AutomationModal;

const styles = EStyleSheet.create({
    contentWrapper: {
        padding: 32,
        width: 600,
        maxWidth: '100%',
        maxHeight: '100vh'
    },
    inputWrapper: {
        marginBottom: 10
    },
    section: {
        marginBottom: 30,
        paddingBottom: 20,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0'
    },
    logicOperatorContainer: {
        marginBottom: 15,
        alignItems: 'center'
    },
    logicOperator: {
        width: 120,
        height: 40,
        paddingVertical: 2,
        alignItems: 'center',
    },
    conditionFieldset: {
        marginBottom: 15,
        padding: 15,
        backgroundColor: '#f8f9fa',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e0e0e0'
    },
    conditionHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    conditionTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#333',
        flex: 1,
        paddingRight: 8,
    },
    conditionActions: {
        flexDirection: 'row',
        alignItems: 'center'
    },
    editButton: {
        backgroundColor: '#007bff',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 4,
        marginRight: 8
    },
    editButtonText: {
        color: 'white',
        fontSize: 12,
        fontWeight: 'bold'
    },
    removeButton: {
        backgroundColor: '#dc3545',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 4
    },
    removeButtonText: {
        color: 'white',
        fontSize: 12,
        fontWeight: 'bold'
    },
    addButton: {
        marginTop: 10,
        alignSelf: 'flex-start'
    },
    sectionTitle: {
        fontSize: 18,
        fontWeight: 'bold',
        color: '#333'
    },
    removeRecipientButton: {
        marginLeft: 8,
        alignSelf: 'center',
    },
    addRecipientButton: {
        marginBottom: 12,
        alignSelf: 'flex-start',
    },
    multilineInputWrapper: {
        height: 'auto',
        minHeight: 142,
        paddingTop: 12,
        paddingBottom: 12,
    },
    multilineInputLabel: {
        marginBottom: 8,
    },
    multilineInput: {
        minHeight: 90,
        textAlignVertical: 'top',
        overflowY: 'auto',
    },
    '@media (max-width: 687px)': {
        contentWrapper: {
            width: '100%',
            maxWidth: '100%',
        },
    },
});
